import type { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { PaidMediaOverviewRevenueValidationPlatform } from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewCurrency,
  formatOverviewMultiplier,
  formatOverviewPct,
} from "../../utils/paidMediaOverviewFormat";
import CustomChartTooltip from "src/components/reusable/CustomChartTooltip/CustomChartTooltip";
import { overviewPlatformLabel } from "../../utils/paidMediaOverviewLabels";
import {
  OverviewChartEmptyState,
  OverviewChartLoadingState,
  OverviewMetricTip,
} from "./overviewPrimitives";
import { OVERVIEW_GRID_COLOR, overviewAxisProps } from "./overviewChartConfig";
import { usePaidMediaChartRtl } from "../../utils/paidMediaChartRtl";

interface RevenueValidationCardProps {
  platforms: PaidMediaOverviewRevenueValidationPlatform[];
  storeRevenueAvailable: boolean;
  currency: string | null;
  loading: boolean;
  campaignFilterActive?: boolean;
}

const RevenueValidationCard: FC<RevenueValidationCardProps> = ({
  platforms,
  storeRevenueAvailable,
  currency,
  loading,
  campaignFilterActive = false,
}) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  const chartData = (platforms ?? []).map(platform => ({
    platform: overviewPlatformLabel(t, platform?.platform),
    platformReported: platform?.platform_reported_revenue ?? null,
    storeGross: platform?.shopify_gross_revenue ?? null,
    storeNet: platform?.shopify_net_revenue ?? null,
  }));

  const renderChart = () => {
    if (loading) {
      return <OverviewChartLoadingState />;
    }
    if (!chartData.length) {
      return <OverviewChartEmptyState />;
    }

    return (
      <ResponsiveContainer>
        <BarChart data={chartData} margin={chartRtl.margin({ top: 8, right: 10, bottom: 5, left: 0 })}>
          <CartesianGrid stroke={OVERVIEW_GRID_COLOR} vertical={false} />
          <XAxis dataKey="platform" reversed={chartRtl.axisReversed} {...overviewAxisProps} />
          <YAxis
            width={88}
            tickFormatter={value => formatOverviewCurrency(value, currency)}
            orientation={chartRtl.yAxisOrientation}
            {...overviewAxisProps}
          />
          <Tooltip
            wrapperClassName="paid-media-overview__chart-tooltip-wrapper"
            content={
              <CustomChartTooltip
                formatTooltip={(value: number) => formatOverviewCurrency(value, currency)}
              />
            }
          />
          <Bar
            dataKey="platformReported"
            name={t("paidMediaOverview.attribution.seriesPlatformReported")}
            className="platform-reported-bar"
            radius={[3, 3, 0, 0]}
          />
          {storeRevenueAvailable ? (
            <Bar
              dataKey="storeGross"
              name={t("paidMediaOverview.attribution.seriesStoreGross")}
              className="store-gross-bar"
              radius={[3, 3, 0, 0]}
            />
          ) : null}
          {storeRevenueAvailable ? (
            <Bar
              dataKey="storeNet"
              name={t("paidMediaOverview.attribution.seriesStoreNet")}
              className="store-net-bar"
              radius={[3, 3, 0, 0]}
            />
          ) : null}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <article className="paid-media-panel paid-media-overview__attribution-chart-card">
      <div className="paid-media-overview__panel-header">
        <div>
          <h3>
            {t("paidMediaOverview.attribution.validationTitle")}
            <OverviewMetricTip
              id="reportingVariance"
              label={t("paidMediaOverview.attribution.validationTitle")}
            />
          </h3>
          <p className="paid-media-overview__panel-subtitle">
            {t("paidMediaOverview.attribution.validationSubtitle")}
          </p>
        </div>
      </div>
      {storeRevenueAvailable && campaignFilterActive ? (
        <p className="paid-media-overview__confidence-footnote" role="status">
          {t("paidMediaOverview.attribution.campaignFilterNotAppliedToStore")}
        </p>
      ) : null}
      <div className="paid-media-overview__chart paid-media-overview__chart--sm paid-media-overview-platform-and-store">
        {renderChart()}
      </div>
      <div
        className="paid-media-overview__chart-legend-row"
        aria-label={t("paidMediaOverview.attribution.legendAria")}
      >
        <div className="paid-media-overview__chart-legend-item paid-media-overview__chart-legend-item--purple">
          <span
            className="paid-media-overview__chart-legend-swatch paid-media-overview__chart-legend-swatch--purple"
            aria-hidden
          />
          <span>{t("paidMediaOverview.attribution.platformReported")}</span>
        </div>
        {storeRevenueAvailable ? (
          <>
            <div className="paid-media-overview__chart-legend-item paid-media-overview__chart-legend-item--green">
              <span
                className="paid-media-overview__chart-legend-swatch paid-media-overview__chart-legend-swatch--green"
                aria-hidden
              />
              <span>{t("paidMediaOverview.attribution.storeGross")}</span>
            </div>
            <div className="paid-media-overview__chart-legend-item paid-media-overview__chart-legend-item--mint">
              <span
                className="paid-media-overview__chart-legend-swatch paid-media-overview__chart-legend-swatch--mint"
                aria-hidden
              />
              <span>{t("paidMediaOverview.attribution.storeNet")}</span>
            </div>
          </>
        ) : null}
      </div>
      {storeRevenueAvailable ? (
        <div className="paid-media-overview__variance-grid">
          {(platforms ?? []).map(platform => (
            <VarianceBox
              key={platform?.platform}
              title={overviewPlatformLabel(t, platform?.platform)}
              variance={platform?.reporting_variance ?? null}
              adjRoas={platform?.refund_adjusted_roas ?? null}
            />
          ))}
        </div>
      ) : (
        <p className="paid-media-overview__confidence-footnote" role="status">
          {t("paidMediaOverview.attribution.storeUnavailable")}
        </p>
      )}
    </article>
  );
};

const VarianceBox: FC<{
  title: string;
  variance: number | null;
  adjRoas: number | null;
}> = ({ title, variance, adjRoas }) => {
  const { t } = useTranslation();
  const hasVariance = variance !== null && Number.isFinite(variance);
  const positive = hasVariance && (variance as number) > 0;
  const varianceTone = !hasVariance
    ? ""
    : positive
      ? "paid-media-overview__text-warning"
      : "paid-media-overview__text-success";

  const varianceDisplay = `${positive ? "+" : ""}${formatOverviewPct(variance)}`;

  return (
    <div className="paid-media-overview__variance-box">
      <div className="paid-media-overview__variance-box-title">{title}</div>
      <div className="paid-media-overview__variance-box-metrics">
        <div>
          <div className="paid-media-overview__variance-box-label">
            {t("paidMediaOverview.attribution.reportingVariance")}
            <OverviewMetricTip
              id="reportingVariance"
              label={t("paidMediaOverview.attribution.reportingVariance")}
            />
          </div>
          <div className={`paid-media-overview__variance-box-value ${varianceTone}`.trim()}>
            {varianceDisplay}
          </div>
        </div>
        <div>
          <div className="paid-media-overview__variance-box-label">
            {t("paidMediaOverview.attribution.refundAdjustedRoas")}
            <OverviewMetricTip
              id="refundAdjustedRoas"
              label={t("paidMediaOverview.attribution.refundAdjustedRoas")}
            />
          </div>
          <div className="paid-media-overview__variance-box-value">
            {formatOverviewMultiplier(adjRoas)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueValidationCard;
